import { FC, useCallback } from 'react';
import { buildCompanyActivityRow } from '../../utils/companyActivityReport';
import DhSheetBulkExport, {
  IDhSheetPlacement,
  IDhSheetRow,
  sheetPlacement,
  sheetSplitting,
} from './DhSheetBulkExport';

// kan-98 part 6: the payment-side sibling of the commission sheet — same page
// of direct-hire placements, but only the ones a check or wire has landed on.

interface ICompanyActivityReportExportProps {
  /** The rows currently loaded in the listing (whatever filters/page apply). */
  placements: Array<any>;
}

const CompanyActivityReportExport: FC<ICompanyActivityReportExportProps> = ({
  placements,
}) => {
  const buildRow = useCallback((placement: IDhSheetPlacement): IDhSheetRow => {
    const { columns, commissionsComplete, hasPayment, hasInvoice } =
      buildCompanyActivityRow({
        placement: sheetPlacement(placement),
        placementSplitting: sheetSplitting(placement),
        commissions: placement?.commissions || [],
        payments: placement?.payments || [],
      });

    // A row needs some billing activity logged — an invoice sent OR money
    // received; only a placement with neither is left out. Coverage is
    // reported first: an unbilled placement is expected to show up later, an
    // uncommissioned one is something to go and fix.
    const billingLogged = hasInvoice || hasPayment;
    return {
      columns,
      include: commissionsComplete && billingLogged,
      skipReason: !commissionsComplete ? 'commissions' : 'payment',
    };
  }, []);

  return (
    <DhSheetBulkExport
      buttonLabel="Company Activity Report"
      modalTitle="Company Activity Report Rows"
      placements={placements}
      buildRow={buildRow}
      emptyMessage="No direct-hire placements on this page have both their commissions fully entered or payment logged"
      skipNotes={{
        commissions: (count) =>
          count === 1
            ? '1 direct-hire placement left out — commissions not fully entered'
            : `${count} direct-hire placements left out — commissions not fully entered`,
        payment: (count) =>
          count === 1
            ? '1 direct-hire placement left out — no invoice or payment logged'
            : `${count} direct-hire placements left out — no invoice or payment logged`,
      }}
    />
  );
};

export default CompanyActivityReportExport;
